import ModalRetorno from '@/modules/Portaria/components/ModalRetorno';
import CustomButton from '@/components/UI/Button';
import ModalBase from '@/components/UI/ModalBase';
import { useQueryListToSelect } from '@/services/adapters/queryListToSelect';
import { api } from '@/services/api';
import { Box, Checkbox, Flex, Spinner, Text } from '@chakra-ui/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useEffect, useMemo, useState } from 'react';
import { FaChevronDown, FaChevronRight, FaFolder, FaKey } from 'react-icons/fa';
import { MdOutlineCheckBox, MdOutlineCheckBoxOutlineBlank } from 'react-icons/md';
import { useNavigate, useParams } from 'react-router-dom';

const isMobileItem = (label: any) => String(label ?? "").startsWith("M_");
const stripMobilePrefix = (label: any) => String(label ?? "").replace(/^M_/, "");
const byOrdem = (a: any, b: any) => (a?.ordem ?? 0) - (b?.ordem ?? 0);

export const AddPermittions = () => {
  const queryClient = useQueryClient();

  const { id: userSelected } = useParams();
  const nav = useNavigate();
  const [isOpen, setIsOpen] = useState(true);
  const [rotinaSelect, setRotinaSelect] = useState<any[]>([]);
  const [rotinasSuccessCreated, setRotinaSuccessCreated] = useState<any[]>([]);
  const [expandedMenus, setExpandedMenus] = useState<Record<string, boolean>>({});
  const [mensagemErro, setMensagemErro] = useState<string | null>(null);
  const [mensagemSucesso, setMensagemSucesso] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const onClose = () => {
    setIsOpen(false);
    nav(-1);
  };

  const { list: listMenuRaw, isLoading: isLoadingMenu } = useQueryListToSelect("menu");
  const { list: listRotinaRaw, isLoading: isLoadingRotina } = useQueryListToSelect("rotina");
  const isLoadingListas = isLoadingMenu || isLoadingRotina;

  const listMenu = useMemo(
    () => [...(listMenuRaw ?? [])].sort(byOrdem),
    [listMenuRaw]
  );

  const listRotina = useMemo(
    () =>
      [...(listRotinaRaw ?? [])]
        .filter((r: any) => String(r?.label ?? "").trim().toLowerCase() !== "outros")
        .sort(byOrdem),
    [listRotinaRaw]
  );

  const idsRotinasVisiveis = useMemo(
    () => new Set((listRotina ?? []).map((r: any) => r.value)),
    [listRotina]
  );

  const listRotinaMobile = useMemo(
    () => (listRotina ?? []).filter((r: any) => isMobileItem(r?.label)),
    [listRotina]
  );

  const listRotinaWeb = useMemo(
    () => (listRotina ?? []).filter((r: any) => !isMobileItem(r?.label)),
    [listRotina]
  );

  const listMenuMobile = useMemo(
    () =>
      (listMenu ?? []).filter((m: any) =>
        listRotinaMobile.some((r: any) => r.menu === m.value)
      ),
    [listMenu, listRotinaMobile]
  );

  const listMenuWeb = useMemo(
    () =>
      (listMenu ?? []).filter((m: any) =>
        listRotinaWeb.some((r: any) => r.menu === m.value)
      ),
    [listMenu, listRotinaWeb]
  );

  const { data: permissoesUsuario } = useQuery({
    queryKey: ["permissao-usuario", userSelected],
    queryFn: () =>
      api
        .get("permissao/filtro", {
          params: { usuario: userSelected, page: 0, size: 1000 },
        })
        .then((res) => res.data.paginacao),
    enabled: !!userSelected,
    staleTime: 1000 * 30,
  });

  const permissoesExistentes = useMemo(() => {
    const mapa: Record<number, { idPermissao: number; ativo: boolean }> = {};
    (permissoesUsuario ?? []).forEach((perm: any) => {
      mapa[perm.rotina.id] = { idPermissao: perm.id, ativo: perm.ativo };
    });
    return mapa;
  }, [permissoesUsuario]);

  const [foiInicializado, setFoiInicializado] = useState(false);
  const [idsIniciais, setIdsIniciais] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (foiInicializado || !listRotina || !permissoesUsuario) return;

    const ativos = listRotina.filter(
      (r: any) => permissoesExistentes[r.value]?.ativo
    );
    setRotinaSelect(ativos);
    setIdsIniciais(new Set(ativos.map((r: any) => r.value)));
    setFoiInicializado(true);
  }, [listRotina, permissoesUsuario, permissoesExistentes, foiInicializado]);

  const hasChanges = useMemo(() => {
    const atuais = new Set(rotinaSelect.map((r: any) => r.value));
    if (atuais.size !== idsIniciais.size) return true;
    for (const id of atuais) if (!idsIniciais.has(id)) return true;
    return false;
  }, [rotinaSelect, idsIniciais]);

  const submitPermissao = async () => {
    try {
      setIsSaving(true);
      const idsSelecionados = new Set(rotinaSelect.map((r: any) => r.value));
      const promises: Promise<any>[] = [];

      rotinaSelect.forEach((rotina: any) => {
        const existente = permissoesExistentes[rotina.value];
        if (!existente) {
          promises.push(
            api.post("permissao", {
              ativo: true,
              rotina: rotina.value,
              usuario: userSelected,
            })
          );
        } else if (!existente.ativo) {
          promises.push(
            api.put(`permissao/${existente.idPermissao}`, {
              ativo: true,
              rotina: rotina.value,
              usuario: userSelected,
            })
          );
        }
      });

      Object.entries(permissoesExistentes).forEach(([rotinaId, info]) => {
        if (!idsRotinasVisiveis.has(Number(rotinaId))) return;

        if (info.ativo && !idsSelecionados.has(Number(rotinaId))) {
          promises.push(
            api.put(`permissao/${info.idPermissao}`, {
              ativo: false,
              rotina: Number(rotinaId),
              usuario: userSelected,
            })
          );
        }
      });

      if (promises.length === 0) {
        setMensagemSucesso("Nenhuma alteração para salvar.");
        return;
      }

      await axios
        .all(promises)
        .then(
          axios.spread((...allData) => {
            allData?.map((el) =>
              setRotinaSuccessCreated((rs) => [...rs, el?.data])
            );
            setMensagemSucesso("Permissões atualizadas com sucesso");

            queryClient.invalidateQueries({
              queryKey: ["permissao-usuario", userSelected],
            });

            return;
          })
        )
        .catch((error) => {
          setMensagemErro("Erro ao atualizar Permissões.");
        });
    } catch (error: any) {
      setMensagemErro(
        JSON.stringify(
          error?.response?.data?.mensagem || "Erro ao realizar operação"
        )
      );
    } finally {
      setIsSaving(false);
    }
  };

  const toggleMenu = (menuId: string) => {
    setExpandedMenus((prev) => ({
      ...prev,
      [menuId]: !prev[menuId],
    }));
  };

  const getMenuRoutines = (menuId: string, rotinaList: any[]) =>
    rotinaList?.filter((r: any) => r?.menu === menuId) || [];

  const isMenuPartiallySelected = (menuId: string, rotinaList: any[]) => {
    const menuRoutines = getMenuRoutines(menuId, rotinaList);
    const someSelected = menuRoutines.some((r: any) =>
      rotinaSelect?.some((selected) => selected?.value === r?.value)
    );

    const allSelected =
      menuRoutines.length > 0 &&
      menuRoutines.every((r: any) =>
        rotinaSelect?.some((selected) => selected?.value === r?.value)
      );
    return someSelected && !allSelected;
  };

  const handleMenuSelect = (menu: any, rotinaList: any[]) => {
    const menuRoutines = getMenuRoutines(menu?.value, rotinaList);

    const allSelected =
      menuRoutines.length > 0 &&
      menuRoutines.every((r: any) =>
        rotinaSelect?.some((selected) => selected?.value === r?.value)
      );

    if (allSelected) {
      setRotinaSelect((prev) =>
        prev.filter(
          (item) => !menuRoutines.some((r: any) => r.value === item.value)
        )
      );
    } else {
      const newRoutines = menuRoutines.filter(
        (r: any) =>
          !rotinaSelect?.some((selected) => selected?.value === r?.value)
      );
      setRotinaSelect((prev) => [...prev, ...newRoutines]);
    }
  };

  const isMenuChecked = (menuId: string, rotinaList: any[]) => {
    const menuRoutines = getMenuRoutines(menuId, rotinaList);
    return (
      menuRoutines.length > 0 &&
      menuRoutines.every((r: any) =>
        rotinaSelect?.some((selected) => selected?.value === r?.value)
      )
    );
  };

  const onCheckRotina = (item: {
    id: number;
    value: string;
    label: string;
    menu: string;
  }) => {
    const isChecked = rotinaSelect?.some(
      (element) => element?.value === item?.value
    );

    if (isChecked) {
      setRotinaSelect((prev) =>
        prev?.filter((el) => el?.value !== item?.value)
      );
    } else {
      setRotinaSelect((prev) => [...prev, item]);
    }
  };

  useEffect(() => {
    if (listMenu && listMenu.length > 0) {
      const initialExpandedState: Record<string, boolean> = {};
      listMenu.forEach((menu: any) => {
        initialExpandedState[menu.value] = false;
      });
      setExpandedMenus(initialExpandedState);
    }
  }, [listMenu]);

  const selectAllPermissions = () => {
    if (
      listRotina &&
      listRotina.length === rotinaSelect.length &&
      listRotina.every((r: any) =>
        rotinaSelect.some((selected) => selected.value === r.value)
      )
    ) {
      setRotinaSelect([]);
    } else {
      setRotinaSelect([...listRotina]);
    }
  };

  const areAllPermissionsSelected = () => {
    return (
      listRotina &&
      rotinaSelect.length === listRotina.length &&
      listRotina.every((r: any) =>
        rotinaSelect.some((selected) => selected.value === r.value)
      )
    );
  };

  const renderTree = (menus: any[], rotinas: any[]) => {
    if (menus.length === 0) {
      return (
        <Text px={2} py={4} fontSize="sm" color="gray.400" textAlign="center">
          Nenhum item disponível.
        </Text>
      );
    }

    return menus.map((menu: any) => {
      const menuRoutines = getMenuRoutines(menu?.value, rotinas);
      const isExpanded = expandedMenus[menu.value];

      return (
        <Box
          key={menu.value}
          mb={2}
          borderWidth="1px"
          borderRadius="md"
          overflow="hidden"
        >
          <Flex
            p={2}
            alignItems="center"
            bg={
              isMenuPartiallySelected(menu.value, rotinas)
                ? "gray.100"
                : "white"
            }
            _hover={{ bg: "gray.50" }}
          >
            <Checkbox
              mr={2}
              isChecked={isMenuChecked(menu.value, rotinas)}
              isIndeterminate={isMenuPartiallySelected(menu.value, rotinas)}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => {
                e.stopPropagation();
                handleMenuSelect(menu, rotinas);
              }}
              colorScheme="blue"
            />

            <div
              className="flex flex-1 items-center cursor-pointer"
              onClick={() => toggleMenu(menu.value)}
            >
              <div className="mr-2">
                <FaFolder color="#4A5568" />
              </div>
              <div className="font-medium">
                {stripMobilePrefix(menu?.label)}
              </div>
            </div>

            <div
              className="cursor-pointer"
              onClick={() => toggleMenu(menu.value)}
            >
              {isExpanded ? (
                <FaChevronDown size={12} color="#4A5568" />
              ) : (
                <FaChevronRight size={12} color="#4A5568" />
              )}
            </div>
          </Flex>

          {isExpanded && (
            <Box pl={10} pr={4} pb={2} pt={1}>
              {menuRoutines.map((rotina: any) => (
                <Flex
                  key={rotina.value}
                  p={2}
                  alignItems="center"
                  _hover={{ bg: "gray.50" }}
                  borderRadius="md"
                >
                  <Checkbox
                    isChecked={rotinaSelect?.some(
                      (r) => r?.value === rotina?.value
                    )}
                    onChange={() => onCheckRotina(rotina)}
                    colorScheme="blue"
                  >
                    <Flex alignItems="center">
                      <Box mr={2}>
                        <FaFolder size={12} color="#718096" />
                      </Box>
                      <Box fontSize="sm">{stripMobilePrefix(rotina?.label)}</Box>
                    </Flex>
                  </Checkbox>
                </Flex>
              ))}
            </Box>
          )}
        </Box>
      );
    });
  };

  return (
    <>
      <ModalBase
        isOpen={isOpen}
        onClose={onClose}
        title="Gerenciar Permissões"
        icon={<FaKey />}
        size="2xl"
        maxHeight="90vh"
      >
        <div className="flex flex-row justify-start items-center gap-2 hover:bg-gray-50 px-2 rounded-md cursor-pointer" onClick={selectAllPermissions}>
          <Box
            boxSize={5}
            ml={"2px"}
            as={
              areAllPermissionsSelected()
                ? MdOutlineCheckBox
                : MdOutlineCheckBoxOutlineBlank
            }
          />
          <Text>Selecionar Todos</Text>
        </div>

        {isLoadingListas || !foiInicializado ? (
          <Flex w="full" py={10} justifyContent="center" alignItems="center">
            <Spinner size="lg" color="blue.500" thickness="3px" />
          </Flex>
        ) : (
          <Box overflow="hidden" maxH="65vh" overflowY="auto">
            <Box p={2}>
              <Text fontSize="sm" fontWeight="bold" color="blue.700" mb={1}>
                Web
              </Text>
              {renderTree(listMenuWeb, listRotinaWeb)}

              <Text fontSize="sm" fontWeight="bold" color="blue.700" mt={4} mb={1}>
                Mobile
              </Text>
              {renderTree(listMenuMobile, listRotinaMobile)}
            </Box>
          </Box>
        )}
        <div className="flex flex-row justify-center items-center gap-2 mt-2 px-2 w-[100%]">
          <CustomButton
            onClick={submitPermissao}
            label="Salvar"
            disabled={!hasChanges}
            loading={isSaving}
          />
        </div>
      </ModalBase>

      {(mensagemErro || mensagemSucesso) && (
        <ModalRetorno
          isOpen={true}
          onClose={() => {
            setMensagemErro(null);
            setMensagemSucesso(null);
            nav(-1);
          }}
          mensagem={mensagemErro || mensagemSucesso || ""}
          tipo={mensagemErro ? "erro" : "sucesso"}
        />
      )}
    </>
  );
};
